import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { DeleteConfirmDialog, DeleteDialog, ProgressDialog, SuccessSaveDataDialog } from "../../../../common/dialogs/index.js";
import { ErrorsDialog, UserLogDialog } from "../../../../common/dialogs/index.js";
import { ADD_MODE, EDIT_MODE, EntityFormPageContent, READ_MODE, getCurrentMode } from "../../../../common/entity-form/index.js";
import useLogs from "../../../../common/hooks/useLogs.js";

import { useIntl } from "react-intl";
import CompensationProcedureFormPageHeader from "./compensation-procedure-form-page-header";
import CompensationProcedureFormPageCompensationProcedure from "./compensation-procedure-form-page-compensation-procedure";
import CompensationProcedureFormViews from "./compensation-procedure-form-views";
import { AppDispatch, RootState } from "../../../../../store";
import {
    clearCurrentCompensationProcedure,
    createCompensationProcedure,
    deleteCompensationProcedure,
    editCompensationProcedure,
    fetchCompensationProcedureDetailed,
    setInitialCurrentCompensationProcedure,
    setSubmit,
    setError,
    setValidationErrors,
} from "../../../../../reducers/admin/compensationProceduresSlice";
import { CompensationProcedureDetailed } from "@/types/compensation-procedures";
import { ErrorDetails } from "types/common/responses/responses-types";
import "./style.less";
import checkIfRowHasMatchWithKey from "../../../../../utils/checkRowHasMatchWithKey.js";

const DIALOG_TYPES = {
    DELETE: "DELETE",
    DELETE_CONFIRM: "DELETE_CONFIRM",
};

const pasteErrorsToRows = ({ form, setForm, validationErrors, setValidationErrors, dispatch }) => {
    const formCopy = JSON.parse(JSON.stringify(form));

    let tab: null | string = null,
        errorIndex = null;

    validationErrors.forEach((error) => {
        if (!error.additional_info?.key) return;

        if (error.field === "risks") {
            const fieldMapping = { risk_id: "riskId" };

            formCopy.risks.forEach((action, index) => {
                const rowMatched = checkIfRowHasMatchWithKey(action, error.additional_info.key, fieldMapping);

                if (rowMatched) {
                    action[`error_${fieldMapping[error.additional_info.field]}`] = error;

                    if (tab === null) tab = "risks";
                    if (errorIndex === null) errorIndex = index;
                }
            });
        }
    });

    setForm(formCopy);

    dispatch(setValidationErrors(null));

    return { errorTab: tab, errorIndex };
};

const CompensationProcedureFormPage = () => {
    const { compensationProcedureId } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const intl = useIntl();
    const navigate = useNavigate();

    const { logs, total, getLogs, openLogs, setOpenLogs, pageNumber, setPageNumber, limitNumber, setLimitNumber } = useLogs(
        "compensationProcedure",
        compensationProcedureId
    );

    const busy = useSelector((state: RootState) => state.admin.compensationProcedures.busy);
    const busyType = useSelector((state: RootState) => state.admin.compensationProcedures.busyType);
    const error = useSelector((state: RootState) => state.admin.compensationProcedures.error);
    const submit = useSelector((state: RootState) => state.admin.compensationProcedures.submit);
    const compensationProcedure = useSelector((state: RootState) => state.admin.compensationProcedures.currentCompensationProcedure);
    const compensationProcedureEditable = useSelector((state: RootState) => state.admin.compensationProcedures.currentCompensationProcedureEditable);
    const compensationProcedureDeleted = useSelector((state: RootState) => state.admin.compensationProcedures.currentCompensationProcedureDeleted);
    const validationErrors = useSelector((state: RootState) => state.admin.compensationProcedures.validationErrors);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [errorScroll, setErrorScroll] = useState<{ errorIndex: number | null; errorTab: string | null } | null>(null);

    const [form, setForm] = useState<CompensationProcedureDetailed>({ id: "", description: "", risks: [], compensatedEmployees: [] });
    const [dialogType, setDialogType] = useState<string | null>(null);

    const mode = getCurrentMode(!compensationProcedureId, compensationProcedureEditable);
    const formDisabled = mode === READ_MODE;
    const backPath = useMemo(() => (mode === ADD_MODE ? ".." : "../.."), [mode]);

    const handleBackClick = useCallback(() => {
        navigate(backPath, { relative: "path" });
    }, [navigate, backPath]);

    useEffect(() => {
        if (compensationProcedureId) {
            dispatch(fetchCompensationProcedureDetailed(compensationProcedureId));
        } else {
            dispatch(setInitialCurrentCompensationProcedure());
        }

        return () => {
            dispatch(clearCurrentCompensationProcedure());
        };
    }, [compensationProcedureId, dispatch]);

    useEffect(() => {
        if (!compensationProcedure) {
            return;
        }

        setForm({
            id: compensationProcedure?.id || "",
            description: compensationProcedure?.description || "",
            risks: compensationProcedure?.risks || [],
            compensatedEmployees: compensationProcedure?.compensatedEmployees || [],
        });
    }, [compensationProcedure]);

    useEffect(() => {
        if (!submit) return;

        dispatch(setSubmit(false));

        if (mode === ADD_MODE || compensationProcedureDeleted) {
            handleBackClick();
        }
    }, [submit, dispatch, mode, compensationProcedureDeleted, handleBackClick]);

    const [formErrors, setFormErrors] = useState<Record<string, ErrorDetails> | null>(null);

    useEffect(() => {
        if (!validationErrors) return;
        const errorScrollData = pasteErrorsToRows({
            form,
            setForm,
            validationErrors,
            setValidationErrors,
            dispatch,
        });

        const formErrosTmp = validationErrors.reduce((accumulator, error) => {
            accumulator[error.field] = error;

            return accumulator;
        }, {});

        setFormErrors(formErrosTmp);
        setErrorScroll(errorScrollData);
    }, [validationErrors, form, dispatch]);

    const handleSaveCompensationProcedure = async () => {
        const formCompensationProcedure = {
            id: form.id?.trim(),
            description: form.description,
            risks: form.risks,
            compensatedEmployees: [],
        };

        if (formCompensationProcedure.id !== form.id) {
            setForm({
                ...form,
                id: formCompensationProcedure.id,
            });
        }
        let result;

        if (mode === EDIT_MODE) {
            result = await dispatch(editCompensationProcedure(formCompensationProcedure));
        } else {
            result = await dispatch(createCompensationProcedure(formCompensationProcedure));
        }
        if (!result?.error) {
            setSuccessDialogOpen(true);
        }
    };

    const handleDeleteClick = () => {
        setDialogType(DIALOG_TYPES.DELETE);
    };

    const handleDialogCancel = () => {
        setDialogType(null);
    };

    // const handleDeleteDialogDelete = () => {
    //     setDialogType(DIALOG_TYPES.DELETE_CONFIRM);
    // };

    const handleDeleteConfirmDialogDelete = () => {
        if (!compensationProcedureId) return;
        dispatch(deleteCompensationProcedure(compensationProcedureId));
        setDialogType(null);
    };

    const handleEditClick = () => {
        if (!compensationProcedureId) return;
        dispatch(fetchCompensationProcedureDetailed(compensationProcedureId));
    };

    if (!compensationProcedure && error) return <Navigate to={backPath} relative="path" />;

    return (
        <>
            {form && (
                <EntityFormPageContent>
                    <CompensationProcedureFormPageHeader
                        mode={mode}
                        compensationProcedureId={compensationProcedureId ?? ""}
                        onBackClick={handleBackClick}
                        onDeleteClick={handleDeleteClick}
                        onSaveClick={handleSaveCompensationProcedure}
                        onEditClick={handleEditClick}
                        onShowLogsClick={getLogs}
                    />

                    <CompensationProcedureFormPageCompensationProcedure
                        form={form}
                        setForm={setForm}
                        mode={mode}
                        disabled={formDisabled}
                        validationErrors={formErrors}
                    />
                    <CompensationProcedureFormViews
                        form={form}
                        setForm={setForm}
                        validationErrors={formErrors}
                        formDisabled={formDisabled}
                        errorScroll={errorScroll}
                        setErrorScroll={setErrorScroll}
                    />
                </EntityFormPageContent>
            )}

            <ProgressDialog progress={""} title="" open={busy} busyType={busyType} />

            <DeleteDialog
                open={dialogType === DIALOG_TYPES.DELETE}
                title={intl.formatMessage({ id: "delete-compensation-procedure-process-dialog.title" })}
                text={intl.formatMessage({ id: "delete-compensation-procedure-dialog.text" }, { name: compensationProcedureId })}
                onCancelClick={handleDialogCancel}
                onDeleteClick={handleDeleteConfirmDialogDelete}
            />

            <DeleteConfirmDialog
                open={dialogType === DIALOG_TYPES.DELETE_CONFIRM}
                title={intl.formatMessage({ id: "delete-compensation-procedure-process-dialog.title" })}
                text={intl.formatMessage({ id: "delete-compensation-procedure-dialog.text" }, { name: compensationProcedureId })}
                onCancelClick={handleDialogCancel}
                onDeleteClick={handleDeleteConfirmDialogDelete}
            />

            <ErrorsDialog
                error={error}
                open={error?.type === "error"}
                onClose={() => {
                    dispatch(setError(null));
                }}
            />

            <UserLogDialog
                title={""}
                total={total}
                pageNumber={pageNumber}
                limitNumber={limitNumber}
                setLimitNumber={setLimitNumber}
                setPageNumber={setPageNumber}
                open={openLogs}
                logs={logs}
                onClose={() => setOpenLogs(false)}
            />
            <SuccessSaveDataDialog
                open={successDialogOpen}
                onClose={() => {
                    setSuccessDialogOpen(false);
                }}
            />
        </>
    );
};

export default CompensationProcedureFormPage;
